import { keepPreviousData } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import {
  ActionsDropdown,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { OverflownText } from 'components/OverflownText'
import { TextPill } from 'components/FbUI/TextPill'
import {
  INVITATION_STATUS_ACCEPTED,
  INVITATION_STATUS_EXPIRED,
  INVITATION_STATUS_PENDING,
} from 'models/user'
import type { InviteList } from 'services/users'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiArrowsClockwise, PiLock, PiMagnifyingGlass } from 'react-icons/pi'
import { toast } from 'react-toastify'
import {
  useListInvitations,
  useResendInvitation,
  useDeleteInvitation,
} from 'services/users'
import { useDebounce } from 'use-debounce'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { InitialsAvatar } from 'components/Tables/CommonTableCells/InitialsAvatar'

export function InvitationsTable() {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 300)
  const [resendingId, setResendingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const resendInvitation = useResendInvitation()
  const deleteInvitation = useDeleteInvitation()

  const { data, isPending } = useListInvitations(
    { search: debouncedSearch || undefined, page_size: 100 },
    { query: { placeholderData: keepPreviousData } }
  )

  const handleResend = async (id: number) => {
    setResendingId(id)
    try {
      await resendInvitation.mutateAsync({ id })
      toast.success('Invitation resent successfully!')
    } catch {
      toast.error('Failed to resend invitation')
    } finally {
      setResendingId(null)
    }
  }

  const handleDelete = async (id: number) => {
    const confirmed = await dialogConfirm(
      'Are you sure you want to delete this invitation? This cannot be undone.',
      'Delete Invitation'
    )
    if (!confirmed) return
    setDeletingId(id)
    try {
      await deleteInvitation.mutateAsync({ id })
      toast.success('Invitation deleted successfully!')
    } catch {
      toast.error('Failed to delete invitation')
    } finally {
      setDeletingId(null)
    }
  }

  const columns = useMemo<ColumnDef<InviteList, any>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        size: 280,
        meta: { isFirstColumn: true },
        cell: (info) => (
          <OverflownText>{info.getValue() as string}</OverflownText>
        ),
      },
      {
        accessorKey: 'user_type',
        header: 'User Type',
        size: 140,
      },
      {
        accessorKey: 'rsm_assignment_name',
        header: 'Sample Reviewer',
        size: 200,
        cell: (info) => (info.getValue() as string) ?? '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: (info) => {
          const value = info.getValue() as string
          if (value === INVITATION_STATUS_ACCEPTED) {
            return (
              <TextPill textColor="#15803d" backgroundColor="#dcfce7">
                Accepted
              </TextPill>
            )
          }
          if (value === INVITATION_STATUS_EXPIRED) {
            return (
              <TextPill textColor="#374151" backgroundColor="#f3f4f6">
                Expired
              </TextPill>
            )
          }
          if (value === INVITATION_STATUS_PENDING) {
            return (
              <TextPill textColor="#a16207" backgroundColor="#fef9c3">
                Pending
              </TextPill>
            )
          }
          return value
        },
      },
      {
        accessorKey: 'invited_by_name',
        header: 'Invited By',
        size: 200,
        cell: ({ row }) => {
          const name = (row.original.invited_by_name as string) ?? ''
          if (!name) return '-'
          const parts = name.trim().split(/\s+/)
          const initials = `${parts[0]?.[0] ?? ''}${
            parts.length > 1 ? parts[parts.length - 1][0] : ''
          }`.toUpperCase()
          return (
            <div className="flex items-center gap-2.5">
              <InitialsAvatar initials={initials} />
              <span className="truncate">{name}</span>
            </div>
          )
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 140,
        cell: (info) => {
          const value = info.getValue() as string
          return value ? new Date(value).toLocaleDateString() : '-'
        },
      },
      {
        accessorKey: 'expires_at',
        header: 'Expires',
        size: 140,
        cell: (info) => {
          const value = info.getValue() as string
          return value ? new Date(value).toLocaleDateString() : '-'
        },
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => {
          const invite = row.original
          if (invite.status === INVITATION_STATUS_ACCEPTED) {
            return (
              <div
                className="flex w-full justify-center text-gray-500"
                title="Invitation accepted — no actions available"
              >
                <PiLock size={16} />
              </div>
            )
          }
          const isBusy = resendingId !== null || deletingId !== null
          return (
            <ActionsDropdown
              className="justify-center"
              disabled={isBusy}
              actions={[
                {
                  label: 'Resend',
                  icon: PiArrowsClockwise,
                  onClick: () => handleResend(invite.id),
                },
                createDeleteAction(() => handleDelete(invite.id)),
              ]}
            />
          )
        },
      },
    ],
    // resendingId/deletingId change the disabled state of the dropdown
    [resendingId, deletingId]
  )

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <Spinner animation="border" />
      </div>
    )
  }

  const invitations = data?.results ?? []

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center justify-between gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by email..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
              </div>
            }
            height={'100%'}
            stickyLastColumn
            data={invitations}
            columns={columns}
            enableColumnResizing
            rowHeight={44}
            headerHeight={40}
            tableKey="settings-invitations-table"
          />
        </DataTableContainer>
      </div>
    </TableLinkStyleProvider>
  )
}
